import { createContext, useContext, useLayoutEffect } from "react";

import { verticalFrom, type Vertical } from "@/lib/verticalCopy";

/**
 * The vertical of the SESSION being rendered, for the two magic-link journeys.
 *
 * ## Why the operator's vertical cannot answer this
 *
 * `/verify/:token` and `/voucher-flow` serve both verticals from one path, and
 * whoever is looking at them is usually not signed in at all. The vertical that
 * matters there belongs to the **session**: `org_industry` off
 * `GET /sessions/:token/status`, which both journeys already fetch (`useSession`;
 * `voucherApi.activate` returns a token that resolves against the same route).
 *
 * ## Why a context and not a hook the shell calls itself
 *
 * `AppShell` picks the look of the chrome (`lib/flowPresentation.ts`), and it is
 * the parent of the route table, but the session lives in the child.
 * `useSession` is plain `useState` + `fetch`, not a react-query cache, so a
 * second call in the shell would issue a **second request** for every
 * applicant on every render of the shell. The child that already has the
 * answer publishes it upward instead.
 *
 * ## What is published
 *
 * - `undefined`: not settled yet (the session is loading). The shell renders a
 *   neutral shell rather than guessing a look and then swapping it.
 * - `null`: settled with no live vertical (an error, an expired link, a demo
 *   route). The shell renders the default look.
 * - a `Vertical`: settled. A recruiting session gets the partner look.
 */
export type PublishSessionVertical = (vertical: Vertical | null | undefined) => void;

/**
 * Default is a no-op, so a page rendered outside `AppShell` (tests, the
 * `ErrorBoundary` fallback) publishes into nothing rather than throwing.
 */
const SessionVerticalPublishContext = createContext<PublishSessionVertical>(
  () => {},
);

export const SessionVerticalPublishProvider =
  SessionVerticalPublishContext.Provider;

/**
 * Publish the session's vertical to the shell for the lifetime of this render.
 *
 * `industry` is the raw wire value (`SessionStatus.org_industry`) of a LIVE
 * session; pass undefined on a demo route, which keeps its own look. `settled`
 * says the page has its answer: the session loaded, or failed, or there is no
 * session to load. Nothing is published before that.
 *
 * An unrecognised value resolves through `verticalFrom` to `"unknown"`, which
 * keeps the default look: a third vertical appearing server-side must degrade,
 * not white-screen.
 *
 * A layout effect, so a page that is settled on its first render (a demo
 * route) moves the shell out of its neutral state before the first paint.
 *
 * Clears on unmount, so navigating away from a candidate's link does not
 * leave the candidate's look on the next page.
 */
export function usePublishSessionVertical(
  industry: string | null | undefined,
  settled: boolean,
): void {
  const publish = useContext(SessionVerticalPublishContext);

  useLayoutEffect(() => {
    if (!settled) return;
    publish(industry ? verticalFrom(industry) : null);
    return () => publish(undefined);
  }, [publish, industry, settled]);
}
